/**
 * {order}の小さな部品（#233）：見出しの状態チップ（色だけで伝えない＝文字で）
 *   下書き（番号ありも。#278）／未送付／承諾待ち n日／承諾待ちの期限切れ（朱）／返送待ち（紙の{orderAck}の返送を待つ）／承諾済 MM/DD／紙で回収 MM/DD
 *   訂正版（#233 2b）：訂正中 n回目（訂正版の下書きを直している）／承諾待ち（訂正）（2回目から・承諾の前）／承諾済 MM/DD（n回目）／紙で回収 MM/DD（n回目）
 *   承諾なしで発行した回（#253）：発行済（2回目からは 発行済（n回目））
 */
import { ORDER_MSG, ORDER_REVISE_MSG, unissuedTitle, type Order, type OrderSummary } from "@genbaryoku/contract";
import { mdSlash } from "../../lib/num.js";

type ChipInput = {
  ackState: Order["ackState"];
  waitingDays: number | null;
  documentNo: string | null;
  acceptedAt?: string | null;
  acceptance?: Order["acceptance"];
  /** 出ている回（2回目からは「（n回目）」・承諾待ちは「（訂正）」） */
  round?: number;
  /** 訂正版の下書きを直している回（あれば「訂正中 n回目」） */
  revisingNo?: number | null;
};

/** 承諾の状態の文字（一覧と見出しで同じ） */
export function orderStateText(o: ChipInput): string {
  if (o.revisingNo) return ORDER_REVISE_MSG.chipRevising.replace("{n}", String(o.revisingNo));
  const round = o.round ?? 1;
  switch (o.ackState) {
    case "draft":
      return o.documentNo ? ORDER_MSG.chipUnissued : ORDER_MSG.chipDraft;
    case "unsent":
      return round >= 2 ? ORDER_REVISE_MSG.chipWaitingRevised : ORDER_MSG.chipUnsent;
    case "waiting":
      return round >= 2 ? ORDER_REVISE_MSG.chipWaitingRevised : ORDER_MSG.chipWaiting.replace("{n}", String(o.waitingDays ?? 0));
    case "expired":
      return ORDER_MSG.chipExpired;
    case "paper_pending":
      return ORDER_MSG.chipPaperPending;
    case "accepted": {
      const md = mdSlash(jstDay(o.acceptance?.at ?? o.acceptedAt ?? null));
      return round >= 2 ? ORDER_REVISE_MSG.chipAcceptedRound.replace("{md}", md).replace("{n}", String(round)) : ORDER_MSG.chipAccepted.replace("{md}", md);
    }
    case "paper": {
      const md = mdSlash(o.acceptance?.receivedOn ?? jstDay(o.acceptedAt ?? null));
      return round >= 2 ? ORDER_REVISE_MSG.chipPaperRound.replace("{md}", md).replace("{n}", String(round)) : ORDER_MSG.chipPaper.replace("{md}", md);
    }
    case "issued":
      return round >= 2 ? `${ORDER_MSG.chipIssued}（${round}回目）` : ORDER_MSG.chipIssued;
  }
}
/** 日時（ISO）→ 日本時間の日（YYYY-MM-DD）。toISOString().slice(0,10) だと UTC の日になり、日本の0〜9時は前の日になる */
export const jstDay = (iso: string | null): string | null => (iso ? new Date(new Date(iso).getTime() + 9 * 3600_000).toISOString().slice(0, 10) : null);
const cls = (s: Order["ackState"], revising: boolean) => (revising ? "chip quiet" : s === "expired" ? "chip shu" : s === "accepted" || s === "paper" || s === "issued" ? "chip ai" : "chip quiet");

export function OrderStateChip({ o }: { o: Pick<Order, "ackState" | "waitingDays" | "documentNo" | "acceptance" | "revision" | "revising"> | Pick<OrderSummary, "ackState" | "waitingDays" | "documentNo" | "acceptedAt" | "revisionNo"> }) {
  // 見出し（Order）：出ている回＝訂正中なら前の回（revising.baseRevisionNo）・そうでなければ今の回。一覧（OrderSummary）は revisionNo
  const isOrder = "revision" in o;
  const revisingNo = isOrder && o.revising ? o.revision.no : null;
  const round = isOrder ? (o.revising ? o.revising.baseRevisionNo : o.revision.no) : o.revisionNo;
  // 番号のある下書きは乗せたときの説明（#276）
  const title = !revisingNo && o.ackState === "draft" && o.documentNo ? unissuedTitle(o.documentNo) : undefined;
  return (
    <span className={cls(o.ackState, !!revisingNo)} data-testid="order-state" data-state={revisingNo ? "revising" : o.ackState} title={title}>
      {orderStateText({ ...o, round, revisingNo })}
    </span>
  );
}
